
import { MapPin, Clock, Compass } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Position } from "@/types/locationTypes";
import { formatCoordinates, formatTimestamp } from "@/utils/golfUtils";
import { Progress } from "@/components/ui/progress";

interface PositionDisplayProps {
  type: "start" | "current" | "end";
  position: Position | null;
  isTracking?: boolean;
  progressPercent?: number;
  distance?: number | null;
}

export const PositionDisplay: React.FC<PositionDisplayProps> = ({ 
  type, 
  position, 
  isTracking, 
  progressPercent, 
  distance 
}) => {
  const { t } = useTranslation();
  // Define display properties based on type
  const getIconColor = () => {
    switch (type) {
      case "start": return "text-green-600";
      case "current": return "text-blue-600";
      case "end": return "text-red-600";
      default: return "";
    }
  };

  const getTitle = () => {
    switch (type) {
      case "start": return t("ballDistance.tracking.startPosition");
      case "current": return t("ballDistance.tracking.currentPosition");
      case "end": return t("ballDistance.tracking.endPosition");
      default: return "";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "start": 
      case "end": 
        return <MapPin className={`h-4 w-4 ${getIconColor()}`} />;
      case "current":
        return <Compass className={`h-4 w-4 ${getIconColor()}`} />;
      default:
        return null;
    }
  };

  const shouldDisplayPosition = () => {
    if (type === "current") return isTracking && position;
    if (type === "end") return !isTracking && position;
    return position;
  };

  if (type === "current" && !isTracking) return null;

  return (
    <div className="p-4 bg-muted rounded-lg">
      <div className="flex items-center gap-2 mb-1">
        {getIcon()}
        <p className="text-sm font-medium">{getTitle()}</p>
      </div>
      <p className="text-sm mb-1">
        {shouldDisplayPosition() ? formatCoordinates(position) : (type === "current" ? t("ballDistance.tracking.calculating") : t("ballDistance.tracking.notDefined"))}
      </p>
      {position && (
        <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{formatTimestamp(position.timestamp)}</span>
        </div>
      )}

      {type === "current" && isTracking && (
        <>
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs mt-1 text-right text-muted-foreground">
            {distance !== null ? `${distance.toFixed(1)}m ${t("ballDistance.tracking.traveled")}` : "0m"}
          </p>
        </>
      )}

      {type === "end" && !isTracking && distance !== null && (
        <div className="mt-3 text-center">
          <p className="text-sm font-medium">{t("ballDistance.tracking.finalDistance")}</p>
          <p className="text-lg font-bold">{distance.toFixed(1)} m</p>
        </div>
      )}
    </div>
  );
};
